/**
 * avatarCache — builds circular avatar images for the live-rep layer.
 *
 * The rep photo comes from a session-gated proxy (/api/coordinator-map/
 * rep-photo/:repId). deck.gl's IconLayer needs a ready image URL synchronously,
 * so we composite each avatar once onto an offscreen canvas (circular clip),
 * cache the resulting data URL by repId, and fall back to an initials avatar
 * when the rep has no photo (proxy 404s) or the image fails to load.
 *
 * The recency ring is drawn as a separate deck.gl layer, so it recolors as the
 * fix ages without touching these cached avatars.
 */

const SIZE = 72; // 2× the ~36px display size for retina crispness
const cache = new Map();   // repId -> data URL (ready)
const pending = new Set(); // repId currently loading

// Deterministic background color for initials fallback, keyed off repId.
const INITIAL_BG = ['#5C6BC0', '#26A69A', '#EC407A', '#7E57C2', '#42A5F5', '#66BB6A', '#FF7043', '#8D6E63'];

function initialsOf(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function makeCanvas() {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  return canvas;
}

function clipCircle(ctx) {
  ctx.beginPath();
  ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
}

/** Circular crop of a loaded <img>, cover-fit and centered. */
function composePhoto(img) {
  const canvas = makeCanvas();
  const ctx = canvas.getContext('2d');
  clipCircle(ctx);
  // cover-fit: scale so the shorter side fills the circle, center the overflow
  const scale = Math.max(SIZE / img.width, SIZE / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (SIZE - w) / 2, (SIZE - h) / 2, w, h);
  return canvas.toDataURL('image/png');
}

/** Initials on a colored disc, used when a rep has no usable photo. */
function composeInitials(repId, name) {
  const canvas = makeCanvas();
  const ctx = canvas.getContext('2d');
  clipCircle(ctx);
  ctx.fillStyle = INITIAL_BG[Math.abs(Number(repId) || 0) % INITIAL_BG.length];
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.fillStyle = '#ffffff';
  ctx.font = `600 ${SIZE * 0.4}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initialsOf(name), SIZE / 2, SIZE / 2 + 1);
  return canvas.toDataURL('image/png');
}

/** Synchronously read a ready avatar data URL, or null if not composed yet. */
export function getAvatar(repId) {
  return cache.get(repId) || null;
}

/**
 * Ensure an avatar exists for a rep. Composes it (async for photos) and calls
 * onReady once it lands in the cache. No-op if already cached or in flight.
 */
export function ensureAvatar(repId, name, onReady) {
  if (cache.has(repId) || pending.has(repId)) return;
  pending.add(repId);

  const finish = (dataUrl) => {
    cache.set(repId, dataUrl);
    pending.delete(repId);
    if (onReady) onReady(repId);
  };

  const img = new Image(); // same-origin proxy → canvas is not tainted
  img.onload = () => finish(composePhoto(img));
  img.onerror = () => finish(composeInitials(repId, name)); // no photo / load error
  img.src = `/api/coordinator-map/rep-photo/${repId}`;
}
